import { api } from '../apiClient';

export const registrationService = {
  // On-page registration (single category)
  onpage: async (registrationData: {
    event_slug: string;
    event_pass_category: number;
    quantity: number;
    name: string;
    email: string;
    phone: string;
    custom_fields?: Record<string, any>;
    additional_attendees?: Array<{
      name: string;
      email?: string;
      phone?: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/onpage/ for on-page registration');
    }
    const response = await api.post('/register/onpage/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('On-page Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Multi-category registration
  multi: async (registrationData: {
    event_slug: string;
    categories: Array<{
      category_id: number;
      quantity: number;
      name: string;
      email: string;
      phone: string;
      custom_fields?: Record<string, any>;
      additional_attendees?: Array<{
        name: string;
        email?: string;
        phone?: string;
        custom_fields?: Record<string, any>;
      }>;
    }>;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/multi/ for multi-category registration');
    }
    const response = await api.post('/register/multi/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Multi-category Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Individual registration (detailed attendee info)
  individual: async (formData: FormData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/individual/ for individual registration');
    }
    const response = await api.post('/register/individual/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('Individual Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Seated registration (with seat selection)
  seated: async (registrationData: {
    event_slug: string;
    category_id: number;
    seats: Array<{
      seat_id: number;
      name: string;
      email: string;
      phone: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/seated/ for seated registration');
    }
    const response = await api.post('/register/seated/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Seated Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Cluster seated registration (with automatic seat assignment)
  clusterSeated: async (registrationData: {
    event_slug: string;
    cluster_id: number;
    quantity: number;
    name: string;
    email: string;
    phone: string;
    custom_fields?: Record<string, any>;
    additional_attendees?: Array<{
      name: string;
      email?: string;
      phone?: string;
      custom_fields?: Record<string, any>;
    }>;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/cluster-seated/ for cluster seated registration');
    }
    const response = await api.post('/register/cluster-seated/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Cluster Seated Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // General registration (simple registration)
  general: async (registrationData: {
    event_slug: string;
    category_id: number;
    name: string;
    email: string;
    phone: string;
    quantity: number;
    custom_fields?: { [key: string]: any };
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/general/ for general registration');
    }
    const response = await api.post('/register/general/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('General Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Team registration (team-based registration)
  team: async (registrationData: {
    event_slug: string;
    category_id: number;
    team_name: string;
    email?: string; // Optional, will use first participant's email if not provided
    phone?: string;
    participants: { [key: number]: any };
    // Dynamic fields based on event and category fields
    [key: string]: any;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/team/ for team registration');
    }
    const response = await api.post('/register/team/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Team Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Get whitelist data for approved users
  getWhitelistData: async (params: {
    event_slug: string;
    category_id: number;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to get whitelist data');
    }
    const response = await api.get(`/register/whitelist/${params.event_slug}/${params.category_id}/`);
    if (process.env.NODE_ENV === 'development') {
      console.log('Whitelist Data API response received:', {
        success: response.data.success,
        data: response.data
      });
    }
    return response.data;
  },

  // Whitelist registration (for approved users to continue to purchase)
  whitelist: async (registrationData: {
    event_slug: string;
    category_id: number;
    quantity: number;
    name: string;
    email: string;
    phone: string;
    custom_fields?: { [key: string]: any };
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/whitelist/ for whitelist registration');
    }
    const response = await api.post('/register/whitelist/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Whitelist Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  },

  // Donation registration (donation-based registration)
  donation: async (registrationData: {
    event_slug: string;
    category_id: number;
    name: string;
    email: string;
    phone: string;
    donation: number;
    message?: string;
    anonymous?: boolean;
  }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Making API request to /register/donation/ for donation registration');
    }
    const response = await api.post('/register/donation/', registrationData);
    if (process.env.NODE_ENV === 'development') {
      console.log('Donation Registration API response received:', {
        success: response.data.success,
        message: response.data.message,
        redirectUrl: response.data.redirect_url
      });
    }
    return response.data;
  }
};

